import React, { useEffect, useState } from "react";
import { Modal, Table, Avatar, Input, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useRequest from "../../../hooks/useRequest";
import useDebounce from "../../../hooks/useDebounce";
import { ShowToast, Severty } from "../../../helper/toast";
import apiPath from "../../../constants/apiPath";

function ViewFollowersModal({ visible, onClose, userId }) {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    request({
      url: `${apiPath.userFollowers}/${userId}?search=${debouncedSearchText}&page=${pagination.current}&limit=${pagination.pageSize}`,
      method: "GET",
      onSuccess: (data) => {
        console.log('Followers API response:', data);
        const list = data?.data?.docs || data?.data?.data || data?.data?.followers || data?.data?.list || data?.data || [];
        const total = data?.data?.totalDocs || data?.data?.totalFollowers || data?.data?.totalCount || data?.data?.total || list.length;
        setFollowers(Array.isArray(list) ? list : []);
        setTotalCount(total);
        setLoading(false);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
        setLoading(false);
      },
    });
  }, [visible, userId, debouncedSearchText, pagination.current, pagination.pageSize, request]);

  useEffect(() => {
    if (!visible) {
      setSearchText("");
      setTotalCount(0);
      setPagination({ current: 1, pageSize: 10 });
    }
  }, [visible]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "avatar",
      key: "avatar",
      width: 80,
      render: (avatar, record) => (
        avatar ? (
          <Avatar src={avatar} />
        ) : (
          <Avatar>{record.name?.charAt(0) || record.username?.charAt(0) || "-"}</Avatar>
        )
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <span style={{ fontWeight: "bold" }}>{name || "-"}</span>
          {record.username && (
            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
              @{record.username}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone, record) => (
        phone ? `${record.countryCode ? `+${record.countryCode}-` : ""}${phone}` : "-"
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => {
            onClose();
            navigate(`/admin/user-manager/view/${record.id || record._id}`);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Modal
      visible={visible}
      title={`Followers (${totalCount})`}
      footer={null}
      onCancel={onClose}
      width={750}
    >
      <Input.Search
        placeholder="Search by name..."
        onChange={handleSearchChange}
        value={searchText}
        style={{ marginBottom: 16 }}
        allowClear
      />
      <Table
        dataSource={followers}
        columns={columns}
        rowKey={(record) => record.id || record._id || record.username}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={(pag) => setPagination({ current: pag.current, pageSize: pag.pageSize })}
      />
    </Modal>
  );
}

export default ViewFollowersModal;
